/** Sender avatar component for trigger preview. */

import { useState } from 'react';

import {
  createFaviconLoadGuard,
  extractDomain,
  getAvatarInitials,
  getFaviconUrl,
  getGravatarUrl,
} from '@/lib/crm/utils/avatar';

/**
 * Sender avatar with gravatar fallback chain: Gravatar -> Favicon -> Initials
 */
export function SenderAvatar({ email, name }: { email: string; name: string }) {
  const [avatarState, setAvatarState] = useState<
    'gravatar' | 'favicon' | 'initials'
  >('gravatar');

  const domain = extractDomain(email);
  const initials = getAvatarInitials(name);

  const handleError = () => {
    if (avatarState === 'gravatar') {
      setAvatarState('favicon');
    } else if (avatarState === 'favicon') {
      setAvatarState('initials');
    }
  };

  if (avatarState === 'initials' || !email) {
    return (
      <div className='w-8 h-8 rounded-full bg-neutral-700 dark:bg-neutral-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0'>
        {initials || '?'}
      </div>
    );
  }

  let imageUrl = null;
  if (avatarState === 'gravatar') {
    imageUrl = getGravatarUrl(email, 64);
  } else if (domain) {
    imageUrl = getFaviconUrl(domain, 64);
  }

  if (!imageUrl) {
    return (
      <div className='w-8 h-8 rounded-full bg-neutral-700 dark:bg-neutral-600 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0'>
        {initials || '?'}
      </div>
    );
  }

  return (
    <div className='w-8 h-8 rounded-full overflow-hidden flex items-center justify-center bg-primary border border-primary flex-shrink-0'>
      <img
        src={imageUrl}
        alt={name}
        className='w-full h-full object-cover'
        onLoad={
          avatarState === 'favicon'
            ? createFaviconLoadGuard(handleError)
            : undefined
        }
        onError={handleError}
      />
    </div>
  );
}
